import { useEffect, useState } from 'react';

import { useLocation } from 'react-router';
import Drawer from '@mui/material/Drawer';

import { useInject } from '@vk-hr-tek/core/ioc';
import { Box } from '@vk-hr-tek/ui/Box';
import { Button } from '@vk-hr-tek/ui/Button';
import { Icon } from '@vk-hr-tek/ui/Icon';
import { AnalyticService, GoalsEnum } from '@vk-hr-tek/core/analytics';

import { useSelector } from '@app/hooks';
import { UserRoleEnum } from '@app/types';

import { selectSideMenu } from '../../slice';
import { useStoreReset } from '../../hooks';
import { selectUser } from '../../../user';
import { AbilityProvider } from '../../AbilityProvider';
import { SidebarMappedItem, SidebarContext } from '../Sidebar';
import { SidebarMenuProcessedItem } from '../../types';

import { MobileMenuLogo } from './MobileMenuLogo';

export const MobileMenu = () => {
  const { pathname } = useLocation();
  const { resetStore } = useStoreReset();

  const analyticService = useInject(AnalyticService);

  const sideMenu = useSelector(selectSideMenu);
  const user = useSelector(selectUser);

  const [drawerOpened, setDrawerOpened] = useState<boolean>(false);

  const handleSetDrawerOpened = (state: boolean) => () => {
    setDrawerOpened(state);
  };

  const handleIsActive = (path: string, href: string): boolean => {
    if (href === '/') {
      return path === href;
    } else if (href === '/settings') {
      return (
        path === href ||
        path.includes('/event-types') ||
        path.includes('/settings')
      );
    } else if (path.startsWith('/personal') && href !== '/news') {
      const currentUserId = pathname.split('/personal/')[1] || '';
      return user?.id !== currentUserId;
    } else {
      return path === href;
    }
  };

  const pageMetaTitle =
    typeof document === 'object' &&
    document !== null &&
    !Array.isArray(document) &&
    document?.title
      ? document.title.split('-')
      : [];

  const handleLogAction = (title: string) => {
    analyticService.registerAction(GoalsEnum.openLink, {
      page: pageMetaTitle[pageMetaTitle.length - 1] || '',
      element: 'Боковое меню',
      block: 'Боковое меню',
      actionType: `Переход на страницу ${title}`,
    });
  };

  const onMenuItemClick = (pageTitle: string) => {
    resetStore();
    handleLogAction(pageTitle);
    setDrawerOpened(false);
  };

  const getItemRole = (menuItem: SidebarMenuProcessedItem): UserRoleEnum => {
    if ('nick' in menuItem && menuItem.nick) {
      return menuItem.nick.startsWith('company')
        ? UserRoleEnum.Company
        : UserRoleEnum.Employee;
    }
    return UserRoleEnum.Employee;
  };

  useEffect(() => {
    if (drawerOpened) {
      handleSetDrawerOpened(false)();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <Box>
      <Button
        size="medium"
        variant="tertiaryBlue"
        onClick={handleSetDrawerOpened(true)}
        className="aqa_mobileMenuBurger"
        icon={<Icon name="menu_outline" size="24" />}
      />
      <Drawer
        open={drawerOpened}
        onClose={handleSetDrawerOpened(false)}
        anchor="left"
      >
        <Box
          maxWidth={324}
          boxSizing="border-box"
          height="100%"
          display="flex"
          flexDirection="column"
          alignItems="stretch"
        >
          <Box
            p="16"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Button
              size="medium"
              variant="tertiaryBlue"
              className="aqa_mobileMenuClose"
              onClick={handleSetDrawerOpened(false)}
              icon={
                <Icon
                  name="cross_large_outline"
                  size="20"
                  color="original.brand.primary"
                />
              }
            />
            <MobileMenuLogo onMenuItemClick={onMenuItemClick} />
          </Box>
          <Box flex={1} overflow="auto" p="16">
            <SidebarContext
              value={{
                sidebarOpened: true,
                isActive: handleIsActive,
                onMenuItemClick,
              }}
            >
              <Box display="flex" flexDirection="column" gap="8">
                {sideMenu?.map((item) => {
                  return (
                    <AbilityProvider key={item.id} role={getItemRole(item)}>
                      <SidebarMappedItem item={item} />
                    </AbilityProvider>
                  );
                })}
              </Box>
            </SidebarContext>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};
